import useSWR from 'swr'
import { guardClient } from '../_axios'

export type DashboardKpi = {
  label: string
  value: string
  target: string
  achievementRate: number
  trendText: string
  trendType: 'up' | 'down'
}

export type DashboardActivity = {
  title: string
  owner: string
  timeAgo: string
}

export type DashboardPipeline = {
  projectName: string
  customer: string
  technology: string
  amount: string
  grossProfit: string
  status: string
  owner: string
}

export type DistributionItem = {
  label: string
  count: number
  percentage: number
}

export type DashboardResponse = {
  data: {
    kpis: DashboardKpi[]
    activities: DashboardActivity[]
    pipelines: DashboardPipeline[]
    staffUtilization: { rate: number; total: number; active: number }
    averageUnitPrice: {
      monthly: string
      trend: string
      trendType: 'up' | 'down'
    }
    standbyStaff: { count: number; names: string[] }
    newCustomers: { count: number; target: number; list: string[] }
    industryDistribution: DistributionItem[]
    languageDistribution: DistributionItem[]
    infraDistribution: DistributionItem[]
    workTypeDistribution: DistributionItem[]
  }
}

const dashboardFetcher = async (
  _urlTail: string
): Promise<DashboardResponse> => {
  // const response = await guardClient.get<DashboardResponse>(_urlTail).then((r) => r.data)
  void guardClient

  return Promise.resolve({
    data: {
      kpis: [
        {
          label: '今月の売上',
          value: '¥12.4M',
          target: '目標: ¥15.0M',
          achievementRate: 82.7,
          trendText: '前月比 +15.3%',
          trendType: 'up'
        },
        {
          label: '今年の売上',
          value: '¥156.8M',
          target: '目標: ¥180.0M',
          achievementRate: 87.1,
          trendText: '前年比 +22.5%',
          trendType: 'up'
        },
        {
          label: '商談数（今月）',
          value: '47件',
          target: '目標: 50件',
          achievementRate: 94,
          trendText: '前月比 +8件',
          trendType: 'up'
        },
        {
          label: '成約率（今月）',
          value: '34.2%',
          target: '目標: 30.0%',
          achievementRate: 100,
          trendText: '前月比 +4.2pt',
          trendType: 'up'
        }
      ],
      activities: [
        {
          title: '株式会社ブルーウェーブテック - 提案書提出',
          owner: '山田太郎',
          timeAgo: '2時間前'
        },
        {
          title: '株式会社ミライソリューションズ - 訪問メモ追加',
          owner: '佐藤花子',
          timeAgo: '5時間前'
        },
        {
          title: '株式会社ナミエンタープライズ - 新規案件登録',
          owner: '田中一郎',
          timeAgo: '1日前'
        }
      ],
      pipelines: [
        {
          projectName: '基幹システム刷新プロジェクト',
          customer: '株式会社ブルーウェーブテック',
          technology: 'Java / AWS',
          amount: '¥8,500,000',
          grossProfit: '¥3,400,000',
          status: '提案中',
          owner: '山田太郎'
        },
        {
          projectName: 'セキュリティ診断サービス',
          customer: '株式会社カエデネット',
          technology: 'Python / AWS',
          amount: '¥2,800,000',
          grossProfit: '¥980,000',
          status: '見積提出済',
          owner: '山田太郎'
        }
      ],
      staffUtilization: { rate: 87.5, total: 48, active: 42 },
      averageUnitPrice: {
        monthly: '¥650,000',
        trend: '前月比 +¥20,000',
        trendType: 'up'
      },
      standbyStaff: {
        count: 6,
        names: [
          '鈴木一郎',
          '高橋次郎',
          '渡辺三郎',
          '伊藤四郎',
          '中村五郎',
          '小林六郎'
        ]
      },
      newCustomers: {
        count: 3,
        target: 5,
        list: [
          '株式会社ナミエンタープライズ',
          '株式会社カエデネット',
          '合同会社グリーンフィールド'
        ]
      },
      industryDistribution: [
        { label: '情報通信業', count: 45, percentage: 37.5 },
        { label: '製造業', count: 28, percentage: 23.3 },
        { label: '金融・保険業', count: 20, percentage: 16.7 },
        { label: 'サービス業', count: 15, percentage: 12.5 },
        { label: 'その他', count: 12, percentage: 10.0 }
      ],
      languageDistribution: [
        { label: 'Java', count: 18, percentage: 30.0 },
        { label: 'Python', count: 12, percentage: 20.0 },
        { label: 'TypeScript', count: 10, percentage: 16.7 },
        { label: 'C#', count: 8, percentage: 13.3 },
        { label: 'Go', count: 6, percentage: 10.0 },
        { label: 'その他', count: 6, percentage: 10.0 }
      ],
      infraDistribution: [
        { label: 'AWS', count: 25, percentage: 41.7 },
        { label: 'Azure', count: 15, percentage: 25.0 },
        { label: 'GCP', count: 8, percentage: 13.3 },
        { label: 'オンプレミス', count: 7, percentage: 11.7 },
        { label: 'その他', count: 5, percentage: 8.3 }
      ],
      workTypeDistribution: [
        { label: '開発', count: 30, percentage: 50.0 },
        { label: '保守・運用', count: 12, percentage: 20.0 },
        { label: 'コンサルティング', count: 8, percentage: 13.3 },
        { label: 'テスト', count: 6, percentage: 10.0 },
        { label: 'インフラ構築', count: 4, percentage: 6.7 }
      ]
    }
  })
}

export const useDashboard = () => {
  const path = '/company/dashboard'
  const { data, mutate } = useSWR(path, dashboardFetcher, {
    onErrorRetry: () => {
      return
    }
  })

  return { data: data?.data, mutate }
}
